import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Edit, Trash2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useNavigate, useLocation, useParams, } from "react-router-dom";
import { PetFormHeader } from "../components/PetFormHeader";
import {
  getPetDetail,
  updatePetDetail,
  getDogBreeds,
  getCatBreeds,
  createTransferRecord,
  updateTransferRecord,
  deleteTransferRecord,
} from "../services/api";
import DatePickerField from "../components/DatePickerField";
import { convertToISO, convertFromISO } from "../utils/dateValidation";
import {
  validateChipNumber,
  validateParityInfo,
  validatePetPhoneNumber,
} from "../utils/validation";
import { useTranslation } from 'react-i18next';
import { get, set } from "lodash";

const Label = ({ children, htmlFor, className, ...props }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className={className}
    variant="body2"
    color="textPrimary"
    {...props}
    sx={{ fontWeight: 700, mb: 1, ...props.sx }}
  >
    {children}
  </Typography>
);

const PetDetail = () => {
  const [detailInfo, setDetailInfo] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { petId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [petData, setPetData] = useState(location.state?.petData);
  const { t, i18n } = useTranslation();
  const [breedsLoading, setBreedsLoading] = useState(true);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [editingTransferIndex, setEditingTransferIndex] = useState(null);
  const [transferButtonLoading, setTransferButtonLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [deletingTransferId, setDeletingTransferId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const [transferForm, setTransferForm] = useState({
    regDate: "",
    transferContact: "",
    regPlace: "",
    transferRemark: "",
    transferOwner: "",
  });
  const [dateErrors, setDateErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Breeds data state
  const [dogBreeds, setDogBreeds] = useState([]);
  const [catBreeds, setCatBreeds] = useState([]);
  const [dogBreedsMapping, setDogBreedsMapping] = useState({});
  const [catBreedsMapping, setCatBreedsMapping] = useState({});

  // Load dog breeds data
  const loadDogBreeds = async (lang) => {
    try {
      const response = await getDogBreeds(lang);
      const breedsData = response.data.result || [];
      setDogBreeds(breedsData);

      // Load the opposite language for mapping
      const oppositeLang = lang === 'en' ? 'zh' : 'en';
      const oppositeResponse = await getDogBreeds(oppositeLang);
      const oppositeBreedsData = oppositeResponse.data.result || [];

      // Create a mapping between the two languages
      const mapping = {};
      breedsData.forEach((breed, index) => {
        if (oppositeBreedsData[index]) {
          mapping[breed] = oppositeBreedsData[index];
          mapping[oppositeBreedsData[index]] = breed;
        }
      });
      setDogBreedsMapping(mapping);
    } catch (err) {
      console.error("Error loading dog breeds:", err);
    }
  };

  // Load cat breeds data
  const loadCatBreeds = async (lang) => {
    try {
      const response = await getCatBreeds(lang);
      const breedsData = response.data.result || [];
      setCatBreeds(breedsData);

      // Load the opposite language for mapping
      const oppositeLang = lang === 'en' ? 'zh' : 'en';
      const oppositeResponse = await getCatBreeds(oppositeLang);
      const oppositeBreedsData = oppositeResponse.data.result || [];

      // Create a mapping between the two languages
      const mapping = {};
      breedsData.forEach((breed, index) => {
        if (oppositeBreedsData[index]) {
          mapping[breed] = oppositeBreedsData[index];
          mapping[oppositeBreedsData[index]] = breed;
        }
      });
      setCatBreedsMapping(mapping);
    } catch (err) {
      console.error("Error loading cat breeds:", err);
    }
  };

  // Load all breeds data
  const loadAllBreeds = async (lang) => {
    try {
      setBreedsLoading(true);
      await Promise.all([loadDogBreeds(lang), loadCatBreeds(lang)]);
    } catch (err) {
      console.error("Error loading breeds:", err);
    } finally {
      setBreedsLoading(false);
    }
  };

  // Translate fatherBreed when language changes or when fatherBreed are loaded
  useEffect(() => {
    if (detailInfo.fatherBreed && petData.animal && !breedsLoading) {
      const currentBreeds = petData.animal === "狗" ? dogBreeds : petData.animal === "貓" ? catBreeds : [];
      const currentMapping = petData.animal === "狗" ? dogBreedsMapping : catBreedsMapping;

      // If the current breed doesn't exist in the current language's options
      if (currentBreeds.length > 0 && !currentBreeds.includes(detailInfo.fatherBreed)) {
        // Try to translate it using the mapping
        if (currentMapping[detailInfo.fatherBreed]) {
          setDetailInfo(prev => ({
            ...prev,
            fatherBreed: currentMapping[detailInfo.fatherBreed]
          }));
        } else {
          // If no mapping found, reset to empty to avoid MUI error
          console.warn(`Breed "${detailInfo.fatherBreed}" not found in current language. Resetting.`);
        }
      }
    }
  }, [i18n.language, dogBreeds, catBreeds, dogBreedsMapping, catBreedsMapping, breedsLoading]);

  // Translate motherBreed when language changes or when motherBreed are loaded
  useEffect(() => {
    if (detailInfo.motherBreed && petData.animal && !breedsLoading) {
      const currentBreeds = petData.animal === "狗" ? dogBreeds : petData.animal === "貓" ? catBreeds : [];
      const currentMapping = petData.animal === "狗" ? dogBreedsMapping : catBreedsMapping;

      // If the current breed doesn't exist in the current language's options
      if (currentBreeds.length > 0 && !currentBreeds.includes(detailInfo.motherBreed)) {
        // Try to translate it using the mapping
        if (currentMapping[detailInfo.motherBreed]) {
          setDetailInfo(prev => ({
            ...prev,
            motherBreed: currentMapping[detailInfo.motherBreed]
          }));
        } else {
          // If no mapping found, reset to empty to avoid MUI error
          console.warn(`Breed "${detailInfo.motherBreed}" not found in current language. Resetting.`);
        }
      }
    }
  }, [i18n.language, dogBreeds, catBreeds, dogBreedsMapping, catBreedsMapping, breedsLoading]);

  // Load breeds when language changes
  useEffect(() => {
    const lang = i18n.language;
    loadAllBreeds(lang);
  }, [i18n.language]);

  // Log petData when it's received
  useEffect(() => {
    if (petData) {
      console.log("Pet data received in PetFormHeader:", petData);
    }
  }, [petData]);

  // Handle field changes with validation
  const handleFieldChange = (field, value) => {
    setDetailInfo({ ...detailInfo, [field]: value });

    // Real-time validation for specific fields
    if (field === "chipId") {
      const chipError = validateChipNumber(value);
      setFieldErrors((prev) => ({
        ...prev,
        chipId: chipError,
      }));
    } else if (field === "motherChip") {
      const chipError = validateChipNumber(value);
      setFieldErrors((prev) => ({
        ...prev,
        motherChip: chipError,
      }));
    } else if (field === "fatherChip") {
      const chipError = validateChipNumber(value);
      setFieldErrors((prev) => ({
        ...prev,
        fatherChip: chipError,
      }));
    } else if (field === "motherParity") {
      const parityError = validateParityInfo(value);
      setFieldErrors((prev) => ({
        ...prev,
        motherParity: parityError,
      }));
    } else if (field === "transferContact") {
      const phoneError = validatePetPhoneNumber(value, false);
      setFieldErrors((prev) => ({
        ...prev,
        transferContact: phoneError,
      }));
    } else {
      // Clear error when field is filled
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (location.pathname.includes("edit") && petData) {
      setIsEditMode(true);
      setDetailInfo({
        ...petData,
        motherDOB: convertFromISO(petData.motherDOB),
        fatherDOB: convertFromISO(petData.fatherDOB),
        transfer: petData.transfer
          ? petData.transfer.map((transfer) => ({
            ...transfer,
            regDate: convertFromISO(transfer.regDate),
          }))
          : [],
      });
    }
  }, [location, petId]);

  // Import the utility function instead of duplicating it
  const convertDateForBackend = (dateString) => {
    return convertToISO(dateString);
  };

  const handleSubmit = async (e) => {
    setButtonLoading(true);
    e.preventDefault();
    if (isEditMode) {
      try {
        // Convert dates to backend-friendly format before sending
        // Exclude transfer array since it's handled separately
        const dataToSend = {
          chipId: detailInfo.chipId,
          placeOfBirth: detailInfo.placeOfBirth,
          motherName: detailInfo.motherName,
          motherBreed: detailInfo.motherBreed,
          motherDOB: convertDateForBackend(detailInfo.motherDOB),
          motherChip: detailInfo.motherChip,
          motherPlaceOfBirth: detailInfo.motherPlaceOfBirth,
          motherParity: detailInfo.motherParity,
          fatherName: detailInfo.fatherName,
          fatherBreed: detailInfo.fatherBreed,
          fatherDOB: convertDateForBackend(detailInfo.fatherDOB),
          fatherChip: detailInfo.fatherChip,
          fatherPlaceOfBirth: detailInfo.fatherPlaceOfBirth,
        };

        const response = await updatePetDetail(petId, dataToSend);
        const petdetail = response.data.form;
        if (response.status === 200) {
          setButtonLoading(false);
          setSuccess(true);
          setBackColor("#4CAF50");
          setUpdateMessage(t('PetUpdated'));
          setTimeout(() => {
            setSuccess(false);
          }, 2000);

          setPetData((petData) => ({
            ...petData,
            chipId: petdetail.chipId,
            placeOfBirth: petdetail.placeOfBirth,
            motherName: petdetail.motherName,
            motherBreed: petdetail.motherBreed,
            motherDOB: petdetail.motherDOB,
            motherChip: petdetail.motherChip,
            motherPlaceOfBirth: petdetail.motherPlaceOfBirth,
            motherParity: petdetail.motherParity,
            fatherName: petdetail.fatherName,
            fatherBreed: petdetail.fatherBreed,
            fatherDOB: petdetail.fatherDOB,
            fatherChip: petdetail.fatherChip,
            fatherPlaceOfBirth: petdetail.fatherPlaceOfBirth,
          }));
        }

      } catch (error) {
        setButtonLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorSavingPet'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        console.error("Error updating pet detail:", error);
      }
    }
  };

  const getAndsetPetTransfer = async (petId) => {
    try {
      const petResponse = await getPetDetail(petId);
      const petTransferRecord = petResponse.data.form;
      const formattedData = {
        ...petTransferRecord,
        motherDOB: convertFromISO(petTransferRecord.motherDOB),
        fatherDOB: convertFromISO(petTransferRecord.fatherDOB),
        transfer: petTransferRecord.transfer
          ? petTransferRecord.transfer.map((transfer) => ({
            ...transfer,
            regDate: convertFromISO(transfer.regDate),
          }))
          : [],
      };
      setDetailInfo((prev) => ({
        ...prev,
        ...formattedData,
      }));
      setPetData((petData) => ({
        ...petData,
        transfer: petTransferRecord.transfer,
      }));
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving record:", error);
    }
  };

  const handleTransferFormSubmit = async () => {
    setTransferButtonLoading(true);
    try {
      const transferData = {
        regDate: convertDateForBackend(transferForm.regDate),
        transferContact: transferForm.transferContact,
        regPlace: transferForm.regPlace,
        transferRemark: transferForm.transferRemark,
        transferOwner: transferForm.transferOwner,
      };

      if (editingTransferIndex === null) {
        // Add new transfer record
        const response = await createTransferRecord(
          petId,
          transferData
        );
      } else {
        // Edit existing transfer record
        const transferToEdit =
          detailInfo.transfer[editingTransferIndex];
        await updateTransferRecord(
          petId,
          transferToEdit._id,
          transferData
        );
      }
      getAndsetPetTransfer(petId);
      setTransferButtonLoading(false);


      setShowTransferForm(false);
      setEditingTransferIndex(null);
      setTransferForm({
        regDate: "",
        transferContact: "",
        regPlace: "",
        transferRemark: "",
        transferOwner: "",
      });
    } catch (error) {
      setTransferButtonLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving transfer record:", error);
    }
  }

  const handleTransferDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteTransferRecord(
        petId,
        deletingTransferId
      );
      getAndsetPetTransfer(petId);
      setShowDeleteModal(false);
      setDeleteLoading(false);
    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      setShowDeleteModal(false);
      setDeleteLoading(false);
      console.error("Error deleting transfer record:", error);
    }
  }


  // Filter breeds based on animal type
  const getBreedsList = () => {
    if (petData?.animal === "狗") {
      return dogBreeds;
    } else if (petData?.animal === "貓") {
      return catBreeds;
    } else {
      return [t("other")];
    }
  };

  const breeds = getBreedsList();
  const isMobile = useMediaQuery("(max-width:900px)");

  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      {/* Header */}
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        {/* Tabs */}
        <PetFormHeader
          currentPageId="details"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        <Box component="form" onSubmit={handleSubmit}>
          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t("DetailInfo")}
          </Typography>
          <Grid
            container
            spacing={3}
            justifyContent="space-between"
            sx={{ px: 3, mb: 4 }}
          >
            {/* First Column */}
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box>
                  {/* Chip ID */}
                  <Label htmlFor="chipId" sx={{ display: "block", mb: 1 }}>
                    {t("ChipNo")}
                  </Label>
                  <TextField
                    id="chipId"
                    name="chipId"
                    placeholder={t("ChipNo")}
                    value={detailInfo.chipId || ""}
                    onChange={(e) =>
                      handleFieldChange("chipId", e.target.value)
                    }
                    fullWidth
                    variant="outlined"
                    error={!!fieldErrors.chipId}
                    helperText={fieldErrors.chipId}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            {/* Second Column */}
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <Box>
                  {/* Place of Birth */}
                  <Label
                    htmlFor="placeOfBirth"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("PlaceOfBirth")}
                  </Label>
                  <TextField
                    id="placeOfBirth"
                    name="placeOfBirth"
                    placeholder={t("PlaceOfBirth")}
                    value={detailInfo.placeOfBirth || ""}
                    onChange={(e) =>
                      setDetailInfo({
                        ...detailInfo,
                        placeOfBirth: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }} />
          </Grid>

          <Box>
            <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
              {t("TransferRecord")}
              <Button
                sx={{
                  ml: 2,
                  fontWeight: 700,
                  minWidth: 120,
                  "&:hover": {
                    backgroundColor: "black",
                    color: "white",
                  },
                }}
                variant="outlined"
                color="text.secondary"
                onClick={() => {
                  setShowTransferForm(true);
                  setEditingTransferIndex(null);
                  setTransferForm({
                    regDate: "",
                    transferContact: "",
                    regPlace: "",
                    transferRemark: "",
                    transferOwner: "",
                  });
                }}
                disabled={showTransferForm}
              >
                + {t("AddNewRecord")}
              </Button>
            </Typography>
            {!showTransferForm && petData.transfer.length === 0 && (
            <Typography fontWeight={700} sx={{ color: "#969696", fontSize: 18, pb: 4, display: "flex", justifyContent: "center"}}>{t("NoTransfer")}</Typography>
          )}
          </Box>

          {detailInfo.transfer && detailInfo.transfer.length > 0 && (
            <Box sx={{ px: 3, mb: 4,}}>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block", } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 1,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 700 }}>{t("RegistrationDate")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("RegistrationLocation")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t("TransferOwner")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("contactNumber")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Remarks")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailInfo.transfer.map((transfer, index) => (
                        <TableRow key={index}>
                          <TableCell>{transfer.regDate}</TableCell>
                          <TableCell>{transfer.regPlace}</TableCell>
                          <TableCell>{transfer.transferOwner}</TableCell>
                          <TableCell>{transfer.transferContact}</TableCell>
                          <TableCell>{transfer.transferRemark}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => {
                                setShowTransferForm(true);
                                setEditingTransferIndex(index);
                                setTransferForm({
                                  ...transfer,
                                  regDate: transfer.regDate,
                                });
                              }}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              onClick={() => { setShowDeleteModal(true); setDeletingTransferId(transfer._id); }}
                              size="small"
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Simple Text View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                {detailInfo.transfer.map((transfer, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 4,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16, color: "#333" }}
                      >
                        轉讓資料
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => {
                            setShowTransferForm(true);
                            setEditingTransferIndex(index);
                            setTransferForm({
                              ...transfer,
                              regDate: transfer.regDate,
                            });
                          }}
                          size="small"
                          sx={{ color: "#FFB60C" }}
                        >
                          <Edit size={20} />
                        </IconButton>
                        <IconButton
                          onClick={async () => { setShowDeleteModal(true); setDeletingTransferId(transfer._id); }}
                          size="small"
                          sx={{ color: "#A0A0A0" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* {t("RegistrationDate")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("RegistrationDate")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {transfer.regDate || "-"}
                        </Typography>
                      </Box>

                      {/* {t("RegistrationLocation")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("RegistrationLocation")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {transfer.regPlace || "-"}
                        </Typography>
                      </Box>

                      {/* {t("TransferOwner")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("TransferOwner")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {transfer.transferOwner || "-"}
                        </Typography>
                      </Box>

                      {/* 聯絡電話 */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("contactNumber")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {transfer.transferContact || "-"}
                        </Typography>
                      </Box>

                      {/* {t("Remarks")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("Remarks")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {transfer.transferRemark || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {showTransferForm && (
            <Grid
              container
              spacing={3}
              justifyContent="space-between"
              sx={{ px: 3, mb: 4 , backgroundColor: "#FFF2E2", py: 3}}
            >
              {/* First Column */}
              <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
                <Box>
                  {/* regDate */}
                  <Label htmlFor="regDate" sx={{ display: "block", mb: 1 }}>
                    {t("RegistrationDate")}
                  </Label>
                  <DatePickerField
                    id="regDate"
                    name="regDate"
                    placeholder="DD/MM/YYYY"
                    value={transferForm.regDate}
                    onChange={(value) =>
                      setTransferForm({
                        ...transferForm,
                        regDate: value,
                      })
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, regDate: error })
                    }
                    error={!!dateErrors.regDate}
                    helperText={dateErrors.regDate}
                  />
                </Box>
                <Box className="mt-4">
                  <Label
                    htmlFor="transferContact"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("contactNumber")}
                  </Label>
                  <TextField
                    id="transferContact"
                    name="transferContact"
                    placeholder={t("contactNumber")}
                    value={transferForm.transferContact}
                    onChange={(e) => {
                      setTransferForm({
                        ...transferForm,
                        transferContact: e.target.value,
                      });
                      // Validate transfer contact
                      const phoneError = validatePetPhoneNumber(
                        e.target.value,
                        false
                      );
                      setFieldErrors((prev) => ({
                        ...prev,
                        transferContact: phoneError,
                      }));
                    }}
                    fullWidth
                    variant="outlined"
                    error={!!fieldErrors.transferContact}
                    helperText={fieldErrors.transferContact}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
              </Grid>
              {/* Second Column */}
              <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
                <Box>
                  <Label htmlFor="regPlace" sx={{ display: "block", mb: 1 }}>
                    {t("RegistrationLocation")}
                  </Label>
                  <TextField
                    id="regPlace"
                    name="regPlace"
                    placeholder={t("RegistrationLocation")}
                    value={transferForm.regPlace}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        regPlace: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
                <Box className="mt-4">
                  <Label
                    htmlFor="transferRemark"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("Remarks")}
                  </Label>
                  <TextField
                    id="transferRemark"
                    name="transferRemark"
                    placeholder={t("Remarks")}
                    value={transferForm.transferRemark}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        transferRemark: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
              </Grid>
              {/* Third Column */}
              <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
                <Box>
                  <Label
                    htmlFor="transferOwner"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("TransferOwner")}
                  </Label>
                  <TextField
                    id="transferOwner"
                    name="transferOwner"
                    placeholder={t("TransferOwner")}
                    value={transferForm.transferOwner}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        transferOwner: e.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Box>
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    mt: 8,
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      color: "#FFB60C",
                      borderColor: "#FFB60C",
                      minWidth: 120,
                      cursor: buttonLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                    onClick={handleTransferFormSubmit}
                  >
                    {t('Save')}
                    {transferButtonLoading && (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CircularProgress size={15} color="inherit" />
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                    onClick={() => {
                      setShowTransferForm(false);
                      setEditingTransferIndex(null);
                      setTransferForm({
                        regDate: "",
                        transferContact: "",
                        regPlace: "",
                        transferRemark: "",
                        transferOwner: "",
                      });
                    }}
                  >
                    {t("cancel")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}

          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t("petMotherInfo")}
          </Typography>

          <Grid
            container
            spacing={3}
            justifyContent="space-between"
            sx={{ px: 3, mb: 4 }}
          >
            {/* First Column */}
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box>
                {/* Mother Name */}
                <Label htmlFor="motherName" sx={{ display: "block", mb: 1 }}>
                  {t("petMotherName")}
                </Label>
                <TextField
                  id="motherName"
                  name="motherName"
                  placeholder={t("petMotherName")}
                  value={detailInfo.motherName || ""}
                  onChange={(e) =>
                    setDetailInfo({
                      ...detailInfo,
                      motherName: e.target.value,
                    })
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
              <Box className="mt-4">
                <Label htmlFor="motherChip" sx={{ display: "block", mb: 1 }}>
                  {t("ChipNo")}
                </Label>
                <TextField
                  id="motherChip"
                  name="motherChip"
                  placeholder={t("ChipNo")}
                  value={detailInfo.motherChip || ""}
                  onChange={(e) =>
                    handleFieldChange("motherChip", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors.motherChip}
                  helperText={fieldErrors.motherChip}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Second Column */}
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box>
                {/* Mother Breed */}
                <Label htmlFor="motherBreed" sx={{ display: "block", mb: 1 }}>
                  {t("breed")}
                </Label>
                <FormControl fullWidth>
                  <Select
                    id="motherBreed"
                    name="motherBreed"
                    value={(() => {
                        // Use the same logic as getBreedsList()
                        const currentBreeds = petData?.animal === "狗" ? dogBreeds :
                          petData?.animal === "貓" ? catBreeds : [t("other")];

                        // If breed exists in current options, use it
                        if (currentBreeds.includes(detailInfo.motherBreed)) {
                          return detailInfo.motherBreed;
                        }

                        // If no breed or doesn't exist in options, return empty
                        return "";
                      })()}
                    onChange={(e) =>
                      setDetailInfo({
                        ...detailInfo,
                        motherBreed: e.target.value,
                      })
                    }
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                    MenuProps={{
                      disableScrollLock: true
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t("PleaseSelect")}
                    </MenuItem>
                    {breeds.map((breed) => (
                      <MenuItem key={breed} value={breed}>
                        {breed}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box className="mt-4">
                <Label
                  htmlFor="motherPlaceOfBirth"
                  sx={{ display: "block", mb: 1 }}
                >
                  {t("PlaceOfBirth")}
                </Label>
                <TextField
                  id="motherPlaceOfBirth"
                  name="motherPlaceOfBirth"
                  placeholder={t("PlaceOfBirth")}
                  value={detailInfo.motherPlaceOfBirth || ""}
                  onChange={(e) =>
                    setDetailInfo({
                      ...detailInfo,
                      motherPlaceOfBirth: e.target.value,
                    })
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box>
                {/* Mother DOB */}
                <Label htmlFor="motherDOB" sx={{ display: "block", mb: 1 }}>
                  {t("DateofBirth")}
                </Label>
                <DatePickerField
                  id="motherDOB"
                  name="motherDOB"
                  placeholder="DD/MM/YYYY"
                  value={detailInfo.motherDOB || ""}
                  onChange={(value) =>
                    setDetailInfo({
                      ...detailInfo,
                      motherDOB: value,
                    })
                  }
                  onError={(error) =>
                    setDateErrors({ ...dateErrors, motherDOB: error })
                  }
                  error={!!dateErrors.motherDOB}
                  helperText={dateErrors.motherDOB}
                />
              </Box>
              <Box className="mt-4">
                <Label htmlFor="motherParity" sx={{ display: "block", mb: 1 }}>
                  {t("FetalInfo")}
                </Label>
                <TextField
                  id="motherParity"
                  name="motherParity"
                  placeholder={t("FetalInfo")}
                  value={detailInfo.motherParity || ""}
                  onChange={(e) =>
                    handleFieldChange("motherParity", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors.motherParity}
                  helperText={fieldErrors.motherParity}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t("petFatherInfo")}
          </Typography>

          <Grid
            container
            spacing={3}
            justifyContent="space-between"
            sx={{ px: 3, mb: 4 }}
          >
            {/* First Column */}
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box>
                {/* Pet Father Name */}
                <Label htmlFor="fatherName" sx={{ display: "block", mb: 1 }}>
                  {t("petFatherName")}
                </Label>
                <TextField
                  id="fatherName"
                  name="fatherName"
                  placeholder={t("petFatherName")}
                  value={detailInfo.fatherName || ""}
                  onChange={(e) =>
                    setDetailInfo({
                      ...detailInfo,
                      fatherName: e.target.value,
                    })
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
              <Box className="mt-4">
                <Label htmlFor="fatherChip" sx={{ display: "block", mb: 1 }}>
                  {t("ChipNumber")}
                </Label>
                <TextField
                  id="fatherChip"
                  name="fatherChip"
                  placeholder={t("ChipNumber")}
                  value={detailInfo.fatherChip || ""}
                  onChange={(e) =>
                    handleFieldChange("fatherChip", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  error={!!fieldErrors.fatherChip}
                  helperText={fieldErrors.fatherChip}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Second Column */}
            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box>
                {/* Father Breed */}
                <Label htmlFor="fatherBreed" sx={{ display: "block", mb: 1 }}>
                  {t("breed")}
                </Label>
                <FormControl fullWidth>
                  <Select
                    id="fatherBreed"
                    name="fatherBreed"
                    value={(() => {
                        // Use the same logic as getBreedsList()
                        const currentBreeds = petData?.animal === "狗" ? dogBreeds :
                          petData?.animal === "貓" ? catBreeds : [t("other")];

                        // If breed exists in current options, use it
                        if (currentBreeds.includes(detailInfo.fatherBreed)) {
                          return detailInfo.fatherBreed;
                        }

                        // If no breed or doesn't exist in options, return empty
                        return "";
                      })()}
                    onChange={(e) =>
                      setDetailInfo({
                        ...detailInfo,
                        fatherBreed: e.target.value,
                      })
                    }
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                    MenuProps={{
                      disableScrollLock: true
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t("PleaseSelect")}
                    </MenuItem>
                    {breeds.map((breed) => (
                      <MenuItem key={breed} value={breed}>
                        {breed}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box className="mt-4">
                <Label
                  htmlFor="fatherPlaceOfBirth"
                  sx={{ display: "block", mb: 1 }}
                >
                  {t("PlaceOfBirth")}
                </Label>
                <TextField
                  id="fatherPlaceOfBirth"
                  name="fatherPlaceOfBirth"
                  placeholder={t("PlaceOfBirth")}
                  value={detailInfo.fatherPlaceOfBirth || ""}
                  onChange={(e) =>
                    setDetailInfo({
                      ...detailInfo,
                      fatherPlaceOfBirth: e.target.value,
                    })
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4} sx={{ maxWidth: 300, width: "100%" }}>
              <Box>
                {/* Father DOB */}
                <Label htmlFor="fatherDOB" sx={{ display: "block", mb: 1 }}>
                  {t("DateofBirth")}
                </Label>
                <DatePickerField
                  id="fatherDOB"
                  name="fatherDOB"
                  placeholder="DD/MM/YYYY"
                  value={detailInfo.fatherDOB || ""}
                  onChange={(value) =>
                    setDetailInfo({
                      ...detailInfo,
                      fatherDOB: value,
                    })
                  }
                  onError={(error) =>
                    setDateErrors({ ...dateErrors, fatherDOB: error })
                  }
                  error={!!dateErrors.fatherDOB}
                  helperText={dateErrors.fatherDOB}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Bottom Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: { xs: 5, md: 10 },
              mr: 3,
              gap: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={buttonLoading}
              sx={{
                backgroundColor: "#FFB60C",
                color: "#050505",
                minWidth: 120,
                "&:hover": { backgroundColor: "#FFB60C" },
                "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                cursor: buttonLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
              onClick={handleSubmit}
            >
              {t('Save')}
              {buttonLoading && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <CircularProgress size={15} color="inherit" />
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="text.secondary"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t("cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Footer />

      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -400,
          backgroundColor: BackColor,
          color: "#fff",
          padding: "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {t('ConfirmDeleteTransfer')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: deleteLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={handleTransferDelete}
          >
            {t('Delete')}
            {deleteLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PetDetail;
